import type { PlanPublic, SessionResponse } from '@plans/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

let csrfToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
};

async function parseError(res: Response): Promise<ApiError> {
  try {
    const data = (await res.json()) as { error?: { code?: string; message?: string } };
    return new ApiError(
      res.status,
      data.error?.code ?? 'UNKNOWN',
      data.error?.message ?? res.statusText,
    );
  } catch {
    return new ApiError(res.status, 'UNKNOWN', res.statusText);
  }
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers = new Headers();
  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (method !== 'GET' && method !== 'HEAD' && csrfToken) {
    headers.set('X-CSRF-Token', csrfToken);
  }

  const res = await fetch(path, {
    method,
    headers,
    credentials: 'include',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (res.status === 401) {
    onUnauthorized?.();
    throw await parseError(res);
  }
  if (!res.ok) {
    throw await parseError(res);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export const authApi = {
  login: (username: string, password: string) =>
    api<SessionResponse>('/api/auth/login', { method: 'POST', body: { username, password } }),
  logout: () => api<{ authenticated: boolean }>('/api/auth/logout', { method: 'POST' }),
  session: () => api<SessionResponse>('/api/auth/session'),
};

export const plansApi = {
  list: () => api<{ plans: PlanPublic[] }>('/api/plans'),
  create: (title: string, published = false, publicPath?: string | null) =>
    api<{ plan: PlanPublic }>('/api/plans', {
      method: 'POST',
      body: { title, published, publicPath },
    }),
  get: (id: string) => api<{ plan: PlanPublic }>(`/api/plans/${id}`),
  patch: (id: string, body: { title?: string; published?: boolean; publicPath?: string | null }) =>
    api<{ plan: PlanPublic }>(`/api/plans/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => api<void>(`/api/plans/${id}`, { method: 'DELETE' }),
  setPassword: (id: string, password: string) =>
    api<{ plan: PlanPublic }>(`/api/plans/${id}/password`, { method: 'PUT', body: { password } }),
  removePassword: (id: string) =>
    api<{ plan: PlanPublic }>(`/api/plans/${id}/password`, { method: 'DELETE' }),
};

export function uploadPlanFiles(
  planId: string,
  files: File[],
  relativePaths: string[],
  onProgress: (percent: number) => void,
): Promise<{ plan: PlanPublic }> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    files.forEach((file, index) => {
      form.append('files', file);
      form.append('paths', relativePaths[index] ?? (file.webkitRelativePath || file.name));
    });

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/plans/${planId}/files`);
    xhr.withCredentials = true;
    if (csrfToken) {
      xhr.setRequestHeader('X-CSRF-Token', csrfToken);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, 'NETWORK', 'Network error during upload'));
    xhr.onload = () => {
      if (xhr.status === 401) {
        onUnauthorized?.();
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText) as { plan: PlanPublic });
        return;
      }
      try {
        const data = JSON.parse(xhr.responseText) as { error?: { code?: string; message?: string } };
        reject(
          new ApiError(
            xhr.status,
            data.error?.code ?? 'UNKNOWN',
            data.error?.message ?? 'Upload failed',
          ),
        );
      } catch {
        reject(new ApiError(xhr.status, 'UNKNOWN', 'Upload failed'));
      }
    };
    xhr.send(form);
  });
}
